/**
 * Admin Profile API Client (Phase 4.3 / rubric O — User Roster)
 *
 * Typed client for the Profile.API admin user roster + account-lifecycle
 * endpoints. Mirrors the fetchJson-over-authenticatedFetch pattern used by
 * orchestratorClient.ts / knowledgeClient.ts — the Bearer token is attached
 * automatically via the app's token provider wiring (see App.tsx /
 * authenticatedFetch).
 *
 * All endpoints are admin-only (require the Admin role in the JWT):
 *   GET  /api/profile/admin/users?search=&page=&pageSize=
 *   GET  /api/profile/admin/users/{id}
 *   POST /api/profile/admin/{id}/suspend    body { reason }
 *   POST /api/profile/admin/{id}/reinstate  body { reason }
 *   POST /api/profile/admin/{id}/disable    body { reason }
 *   POST /api/profile/admin/{id}/restore    body { reason }
 */

import { authenticatedFetch } from './authenticatedFetch';
import type { RetryConfig } from './resilientFetch';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

async function fetchJson<T>(
  url: string,
  init?: RequestInit,
  retryConfig?: Partial<RetryConfig>,
): Promise<T> {
  const request: RequestInit = {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  };
  const response = retryConfig
    ? await authenticatedFetch(url, request, { retryConfig })
    : await authenticatedFetch(url, request);

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
  }

  return response.json() as Promise<T>;
}

function buildQuery(params: Record<string, string | number | undefined | null>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      qs.set(key, String(value));
    }
  }
  const str = qs.toString();
  return str ? `?${str}` : '';
}

// Mirrors the per-service base-URL pattern in agentAdminService.ts
// (VITE_SERVICEMANAGER_API_URL / SERVICEMANAGER_BASE): the admin SWA builds
// with VITE_API_URL pointed directly at TaskManager.API, not the gateway, so
// these Profile.API-only routes 404'd live. Unlike Journals (adminModerationService.ts),
// Profile.API serves this route at the SAME path (no prefix to strip), so we
// only need to prepend the host. Falls back to the relative path for local
// dev (Vite proxy / gateway rewrite).
const PROFILE_BASE: string = import.meta.env.VITE_PROFILE_API_URL || '';
const PROFILE_ADMIN_BASE = `${PROFILE_BASE}/api/profile/admin`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** AccountState enum values as stored on the Profile record. */
export enum AccountState {
  Guest = 0,
  Pending = 1,
  Active = 2,
  Suspended = 3,
  Disabled = 4,
}

export interface AdminUserRosterItemDto {
  profileId: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  accountState: number;
  accountStateName: string;
  createdAt: string;
  deletedAt?: string | null;
  subscriptionTier: string;
}

/** Paged list envelope returned by the roster list endpoint. */
export interface AdminUserRosterPagedResult {
  total: number;
  page: number;
  pageSize: number;
  items: AdminUserRosterItemDto[];
}

export interface GetUsersParams {
  /** Free-text search across name / email. */
  search?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Thrown when the API responds 404 — the profileId was not found. The Profile.API
 * returns `{ error: 'ProfileNotFound' }` on both the detail GET and the lifecycle
 * POST endpoints.
 */
export class ProfileNotFoundError extends Error {
  constructor(profileId: string) {
    super(`Profile not found: ${profileId}`);
    this.name = 'ProfileNotFoundError';
  }
}

// ---------------------------------------------------------------------------
// List + detail
// ---------------------------------------------------------------------------

export function getUsers(
  params: GetUsersParams = {},
  retryConfig?: Partial<RetryConfig>,
): Promise<AdminUserRosterPagedResult> {
  const qs = buildQuery({
    search: params.search,
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 50,
  });
  return fetchJson<AdminUserRosterPagedResult>(
    `${PROFILE_ADMIN_BASE}/users${qs}`,
    undefined,
    retryConfig,
  );
}

export async function getUser(id: string): Promise<AdminUserRosterItemDto> {
  const response = await authenticatedFetch(
    `${PROFILE_ADMIN_BASE}/users/${encodeURIComponent(id)}`,
    { headers: { 'Content-Type': 'application/json' } },
  );

  if (response.status === 404) {
    throw new ProfileNotFoundError(id);
  }

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
  }

  return response.json() as Promise<AdminUserRosterItemDto>;
}

// ---------------------------------------------------------------------------
// Lifecycle actions — each takes a reason (min ~10 chars, enforced by the UI).
// The API returns 204 No Content on success, 404 when the profile is missing.
// ---------------------------------------------------------------------------

async function lifecycleAction(id: string, action: string, reason: string): Promise<void> {
  const response = await authenticatedFetch(
    `${PROFILE_ADMIN_BASE}/${encodeURIComponent(id)}/${action}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    },
  );

  if (response.status === 404) {
    throw new ProfileNotFoundError(id);
  }

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
  }
}

export function suspendUser(id: string, reason: string): Promise<void> {
  return lifecycleAction(id, 'suspend', reason);
}

export function reinstateUser(id: string, reason: string): Promise<void> {
  return lifecycleAction(id, 'reinstate', reason);
}

export function disableUser(id: string, reason: string): Promise<void> {
  return lifecycleAction(id, 'disable', reason);
}

export function restoreUser(id: string, reason: string): Promise<void> {
  return lifecycleAction(id, 'restore', reason);
}
